import { IkeFieldType, isMeasurableIkePhotoFieldType, type SubFormSpanTypeState, SubFormType } from '@ikegps/core-api';
import type { FormModelField } from '../../form.field';
import { FormModelFieldSelectList } from '../../form.select.list';
import type { FormModelFieldSubForm } from '../form.subform';
import { getOtherAssignedSubformFieldIds } from '../form.subform.field.util';
import { SPAN_ENVIRONMENT_VALUES } from './form.subform.span';
import { type SpanRole, SpanTypeOptionSet } from './span.type.option.set';

export type SpanRoleStatus = 'valid' | 'unassigned' | 'missingField' | 'invalidField';

/**
 * Read model judging the health of a span subform's assigned fields.
 * Eligibility checks are lenient (either style's values); assignment validation is form-style-strict.
 */
export class SpanSubFormValidator {
	constructor(private subForm: FormModelFieldSubForm) {}

	private get state(): SubFormSpanTypeState | null {
		return this.subForm.subFormSpanTypeState;
	}

	private get optionSet(): SpanTypeOptionSet {
		return SpanTypeOptionSet.forForm(this.subForm.getForm());
	}

	isEligibleVectorField(field: FormModelField): boolean {
		const otherIds = getOtherAssignedSubformFieldIds(field, this.state?.vectorFieldId);
		return field.type === IkeFieldType.VECTOR && !otherIds.includes(field.id);
	}

	isEligibleTypeField(field: FormModelField): boolean {
		if (!FormModelFieldSelectList.isSelectList(field)) {
			return false;
		}
		const otherIds = getOtherAssignedSubformFieldIds(field, this.state?.typeFieldId);
		if (otherIds.includes(field.id)) {
			return false;
		}
		const optionSet = this.optionSet;
		return optionSet.requiredRoles.every((role) =>
			field.options.some((option) => optionSet.roleForAnyStyleValue(option.value) === role)
		);
	}

	isEligibleEnvironmentField(field: FormModelField): boolean {
		const otherIds = getOtherAssignedSubformFieldIds(field, this.state?.environmentFieldId);
		return FormModelFieldSelectList.isSelectList(field) && !otherIds.includes(field.id);
	}

	isDefaultEnvironmentField(field: FormModelField): boolean {
		if (!this.isEligibleEnvironmentField(field) || !FormModelFieldSelectList.isSelectList(field)) {
			return false;
		}
		const fieldValues = field.options.slice(1).map((option) => option.value);
		return (
			fieldValues.length === SPAN_ENVIRONMENT_VALUES.length &&
			SPAN_ENVIRONMENT_VALUES.every((v) => fieldValues.includes(v))
		);
	}

	isEligibleMidSpanPhotoField(field: FormModelField): boolean {
		const otherIds = getOtherAssignedSubformFieldIds(field, this.state?.midSpanIkePhotoFieldId);
		return SpanSubFormValidator.isMidSpanPhotoType(field) && !otherIds.includes(field.id);
	}

	/**
	 * Both photo types are accepted in this role, not just the dedicated tool. Existing customer forms all
	 * carry a TRUE_SIZE mid span photo; narrowing this to MID_SPAN would flip `isValid` / `canLink` /
	 * `hasCompleteState` to false on every one of them at load.
	 */
	static isMidSpanPhotoType(field: FormModelField): boolean {
		return isMeasurableIkePhotoFieldType(field.type);
	}

	get vectorStatus(): SpanRoleStatus {
		return this.fieldStatus(this.state?.vectorFieldId, (field) => field.type === IkeFieldType.VECTOR);
	}

	get typeStatus(): SpanRoleStatus {
		const id = this.state?.typeFieldId;
		if (!id) {
			return 'unassigned';
		}
		const field = this.subForm.getOptionalField(id);
		if (!field) {
			return 'missingField';
		}
		if (!FormModelFieldSelectList.isSelectList(field)) {
			return 'invalidField';
		}
		return this.missingOrWrongRoles.length === 0 ? 'valid' : 'invalidField';
	}

	get environmentStatus(): SpanRoleStatus {
		return this.fieldStatus(this.state?.environmentFieldId, (field) => FormModelFieldSelectList.isSelectList(field));
	}

	get midSpanPhotoStatus(): SpanRoleStatus {
		return this.fieldStatus(this.state?.midSpanIkePhotoFieldId, SpanSubFormValidator.isMidSpanPhotoType);
	}

	private fieldStatus(id: string | undefined, isValidType: (field: FormModelField) => boolean): SpanRoleStatus {
		if (!id) {
			return 'unassigned';
		}
		const field = this.subForm.getOptionalField(id);
		if (!field) {
			return 'missingField';
		}
		return isValidType(field) ? 'valid' : 'invalidField';
	}

	/** Required roles the assigned type field does not satisfy under this form's style. */
	get missingOrWrongRoles(): SpanRole[] {
		const optionSet = this.optionSet;
		const id = this.state?.typeFieldId;
		const field = id ? this.subForm.getOptionalField(id) : null;
		if (!field || !FormModelFieldSelectList.isSelectList(field)) {
			return optionSet.requiredRoles;
		}
		return optionSet.requiredRoles.filter(
			(role) => !field.options.some((option) => optionSet.roleForValue(option.value) === role)
		);
	}

	/** True when every required assignment resolves to a healthy field. Non-span subforms are always valid. */
	get isValid(): boolean {
		if (this.subForm.subFormType !== SubFormType.SPAN) {
			return true;
		}
		if (!this.state) {
			return false;
		}
		return (
			this.vectorStatus === 'valid' &&
			this.typeStatus === 'valid' &&
			this.midSpanPhotoStatus === 'valid' &&
			this.environmentStatus !== 'missingField' &&
			this.environmentStatus !== 'invalidField'
		);
	}

	/** True when the assigned type field exists but its option set fails strict validation. */
	get isTypeFieldRepairable(): boolean {
		const id = this.state?.typeFieldId;
		if (!id) {
			return false;
		}
		const field = this.subForm.getOptionalField(id);
		return !!field && FormModelFieldSelectList.isSelectList(field) && this.missingOrWrongRoles.length > 0;
	}

	/** Span linking only depends on the vector and type assignments being intact. */
	get canLink(): boolean {
		if (this.subForm.subFormType !== SubFormType.SPAN) {
			return true;
		}
		return this.vectorStatus === 'valid' && this.typeStatus === 'valid';
	}

	/** All field ids required for two-way linking are configured (environment is optional). */
	get hasCompleteState(): boolean {
		const state = this.state;
		if (!state) {
			return false;
		}
		return !!(state.vectorFieldId && state.typeFieldId && state.midSpanIkePhotoFieldId);
	}
}
